import { TIME_MODIFIERS, TimeModifierCode } from "@/types/db/time_cards";

/** A slice of worked time used for billing/payroll calculation. */
export interface TimeSegment {
  start_time?: string;
  end_time?: string;
  duration?: number; // hours
  type?: string; // "worked", "meal", etc.
  billable?: boolean;
  payable?: boolean;
  modifiers?: TimeModifierCode[];
}

/**
 * Total multiplier for a set of time modifiers. Modifiers stack additively:
 * base 1.0 + Σ(multiplier − 1).
 * @example
 * getModifierMultiplier(undefined)       // => 1
 * getModifierMultiplier(["DOT", "HOL"])  // => 2   (1 + 0.5 + 0.5, if both are 1.5×)
 */
export function getModifierMultiplier(modifiers: TimeModifierCode[] | undefined): number {
  if (!modifiers || modifiers.length === 0) return 1;
  return modifiers.reduce((total, code) => {
    const modifier = TIME_MODIFIERS[code];
    return total + (modifier ? modifier.multiplier - 1 : 0);
  }, 1);
}

/**
 * Resolve the effective payroll rate from a position's rate formula + the user's pay.
 * Position formula (`hourly`/`daily`/`base_rate`×mult/`base_add`+offset) is the base;
 * a per-position override wins only if higher; an override with no formula is used directly.
 * @example
 * resolveEffectivePayrollRate({ rate: "hourly", amount: 20 }, null)                       // => { rate: "hourly", amount: 20 }
 * resolveEffectivePayrollRate({ rate: "base_rate", multiplier: 1.5 }, { meta_data: { rate: 20 } }) // => { rate: "hourly", amount: 30 }
 * resolveEffectivePayrollRate({ rate: "hourly", amount: 20 }, { meta_data: { rate: { amount: 25 } } }) // => { rate: "hourly", amount: 25 } (override higher)
 */
export function resolveEffectivePayrollRate(
  payrollRate: any,
  userPaySnapshot: any,
): { rate: "hourly" | "daily"; amount: number } | null {
  const rawUserRate = userPaySnapshot?.meta_data?.rate;
  const hasOverride = typeof rawUserRate === "object" && rawUserRate !== null;
  const overrideAmount = hasOverride ? rawUserRate?.amount || null : null;
  const baseUserRate = hasOverride ? null : typeof rawUserRate === "number" ? rawUserRate : null;

  let positionResult: { rate: "hourly" | "daily"; amount: number } | null = null;

  if (payrollRate) {
    if (payrollRate.rate === "hourly") {
      positionResult = { rate: "hourly", amount: payrollRate.amount || 0 };
    } else if (payrollRate.rate === "daily") {
      positionResult = { rate: "daily", amount: payrollRate.amount || 0 };
    } else if (payrollRate.rate === "base_rate" && baseUserRate) {
      positionResult = { rate: "hourly", amount: baseUserRate * (payrollRate.multiplier || 1) };
    } else if (payrollRate.rate === "base_add" && baseUserRate) {
      positionResult = { rate: "hourly", amount: baseUserRate + (payrollRate.offset || 0) };
    }
  }

  if (overrideAmount && positionResult) {
    if (overrideAmount > positionResult.amount) {
      return { rate: "hourly", amount: overrideAmount };
    }
    return positionResult;
  }

  if (overrideAmount && !positionResult) {
    return { rate: "hourly", amount: overrideAmount };
  }

  return positionResult;
}

/**
 * Compute billing + payroll amounts from a timecard's `time` (segments, or a legacy
 * start/end block). Hourly = hours × rate × modifiers; daily = flat amount if any
 * billable/payable time exists. Amounts round to cents; 0 collapses to null.
 * @example
 * calculateAmountsFromSegments(
 *   { segments: [{ duration: 8, billable: true, payable: true }] },
 *   { rate: "hourly", amount: 50 },
 *   { rate: "hourly", amount: 20 },
 * ) // => { billingAmount: 400, payrollAmount: 160 }
 */
export function calculateAmountsFromSegments(
  time: any,
  billingRate: any,
  payrollRate: { rate: "hourly" | "daily"; amount: number } | null,
): { billingAmount: number | null; payrollAmount: number | null } {
  const hasSegments = time?.segments && Array.isArray(time.segments) && time.segments.length > 0;
  const hasLegacyTime = !hasSegments && time?.start_time && time?.end_time;

  if (!hasSegments && !hasLegacyTime) {
    return { billingAmount: null, payrollAmount: null };
  }

  let segments: any[];
  if (hasSegments) {
    segments = time.segments;
  } else {
    let totalMs = new Date(time.end_time).getTime() - new Date(time.start_time).getTime();
    if (time.meal_start && time.meal_end) {
      totalMs -= new Date(time.meal_end).getTime() - new Date(time.meal_start).getTime();
    }
    segments = [
      {
        start_time: time.start_time,
        end_time: new Date(new Date(time.start_time).getTime() + totalMs).toISOString(),
        billable: true,
        payable: true,
        modifiers: time.modifiers || [],
      },
    ];
  }

  let totalBillingAmount = 0;
  let totalPayrollAmount = 0;
  let hasBillableTime = false;
  let hasPayableTime = false;

  for (const segment of segments) {
    let segmentHours: number;
    if (typeof segment.duration === "number") {
      segmentHours = Math.round(segment.duration * 100) / 100;
    } else if (segment.start_time && segment.end_time) {
      const start = new Date(segment.start_time);
      const end = new Date(segment.end_time);
      segmentHours = Math.round(((end.getTime() - start.getTime()) / (1000 * 60 * 60)) * 100) / 100;
    } else {
      continue;
    }

    const modifierMultiplier = getModifierMultiplier(segment.modifiers);

    const isBillable = segment.billable !== false;
    const isPayable = segment.payable !== false;

    if (isBillable) hasBillableTime = true;
    if (isPayable) hasPayableTime = true;

    if (isBillable && billingRate?.rate === "hourly" && billingRate?.amount) {
      totalBillingAmount += segmentHours * billingRate.amount * modifierMultiplier;
    }

    if (isPayable && payrollRate?.rate === "hourly" && payrollRate?.amount) {
      totalPayrollAmount += segmentHours * payrollRate.amount * modifierMultiplier;
    }
  }

  if (billingRate?.rate === "daily" && billingRate?.amount && hasBillableTime) {
    totalBillingAmount = billingRate.amount;
  }

  if (payrollRate?.rate === "daily" && payrollRate?.amount && hasPayableTime) {
    totalPayrollAmount = payrollRate.amount;
  }

  return {
    billingAmount: totalBillingAmount > 0 ? Math.round(totalBillingAmount * 100) / 100 : null,
    payrollAmount: totalPayrollAmount > 0 ? Math.round(totalPayrollAmount * 100) / 100 : null,
  };
}

/**
 * Lay a timecard's segments on an actual timeline: each segment gets a computed
 * `start_time`/`end_time` by walking a cursor from the timecard's start_time,
 * advancing by EVERY segment's duration — billable and non-billable alike — so a
 * non-billable segment (e.g. a meal) pushes later segments forward instead of
 * the billable time stacking. Any explicit per-segment start/end is preserved.
 *
 * A legacy start/end block (no `segments`) collapses to one full-span segment.
 *
 * @example
 * // worked 4h, meal 1h (non-billable), worked 4h starting 09:00 → the second
 * // worked segment starts at 14:00 (after the meal), not 13:00.
 * withSegmentTimes({ time: { start_time: "2026-01-01T09:00:00.000Z", segments: [
 *   { duration: 4, billable: true }, { duration: 1, billable: false }, { duration: 4, billable: true },
 * ] } });
 */
export function withSegmentTimes(timecard: any): TimeSegment[] {
  const time = typeof timecard.time === "string" ? JSON.parse(timecard.time) : timecard.time;

  if (!time?.segments || !Array.isArray(time.segments)) {
    if (time?.start_time && time?.end_time) {
      let totalMs = new Date(time.end_time).getTime() - new Date(time.start_time).getTime();
      if (time.meal_start && time.meal_end) {
        totalMs -= new Date(time.meal_end).getTime() - new Date(time.meal_start).getTime();
      }
      return [
        {
          start_time: time.start_time,
          end_time: time.end_time,
          duration: totalMs / (1000 * 60 * 60),
          billable: true,
          payable: true,
          modifiers: time.modifiers || [],
        },
      ];
    }
    return [];
  }

  let cursor = time.start_time ? new Date(time.start_time).getTime() : null;
  return time.segments.map((seg: TimeSegment) => {
    const duration = getSegmentDuration(seg);
    const start_time = seg.start_time ?? (cursor != null ? new Date(cursor).toISOString() : undefined);
    const end_time =
      seg.end_time ?? (cursor != null ? new Date(cursor + duration * 3_600_000).toISOString() : undefined);
    if (cursor != null) cursor += duration * 3_600_000;
    return { ...seg, start_time, end_time };
  });
}

/** Billable segments of a timecard, each with a computed actual start/end time. */
export function extractBillableSegments(timecard: any): TimeSegment[] {
  return withSegmentTimes(timecard).filter((s) => s.billable !== false);
}

/** Payable segments of a timecard, each with a computed actual start/end time. */
export function extractPayableSegments(timecard: any): TimeSegment[] {
  return withSegmentTimes(timecard).filter((s) => s.payable !== false);
}

/**
 * Duration of a single segment in hours (from `duration`, else start/end bounds).
 * @example
 * getSegmentDuration({ duration: 2.5 })                                       // => 2.5
 * getSegmentDuration({ start_time: "2026-01-01T09:00:00Z", end_time: "2026-01-01T11:00:00Z" }) // => 2
 */
export function getSegmentDuration(segment: TimeSegment): number {
  if (typeof segment.duration === "number") {
    return segment.duration;
  }
  if (segment.start_time && segment.end_time) {
    const start = new Date(segment.start_time);
    const end = new Date(segment.end_time);
    return (end.getTime() - start.getTime()) / (1000 * 60 * 60);
  }
  return 0;
}
